// Nyafaz demo API client. Talks only to REACT_APP_DEMO_ENGAGEMENT_API_URL.
function defaultDemoApiBase() {
  if (typeof window === 'undefined') return 'http://localhost:8021';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8021';
  }
  return `${window.location.origin}/enterprise-engagement-api`;
}

const BASE = (
  process.env.REACT_APP_ENTERPRISE_ENGAGEMENT_API_URL ||
  process.env.REACT_APP_DEMO_ENGAGEMENT_API_URL ||
  defaultDemoApiBase()
).replace(/\/+$/, '');

export const SALON_NAME = process.env.REACT_APP_NYAFAZ_ENGAGEMENT_SALON_NAME || 'Nyafaz';

/** A link the browser can open: a backend path (such as a call recording kept on the server) gets the API address. */
export function backendUrl(url) {
  return url && url.startsWith('/') ? `${BASE}${url}` : url;
}

function query(params = {}) {
  const clean = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return clean.length ? `?${new URLSearchParams(clean)}` : '';
}

async function request(path, { method = 'GET', body, form } = {}) {
  const init = { method, headers: {} };
  if (form) {
    init.body = form;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${BASE}${path}`, init);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    let detail = data && typeof data === 'object' ? data.detail : data;
    if (Array.isArray(detail)) detail = detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
    const err = new Error(typeof detail === 'string' && detail ? detail : `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Coerce list endpoints to arrays (handles null, or `{ staff: [...] }` wrappers from proxies). */
export function ensureArray(data, extraKeys = []) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const keys = [...extraKeys, 'items', 'data', 'results', 'rows', 'list'];
    for (const key of keys) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

// Backend API version this frontend needs (see API_VERSION in app/main.py).
export const REQUIRED_API_VERSION = 10;

const api = {
  health: () => request('/health'),
  summary: () => request('/summary'),

  // Staff and slots (Staff Availability)
  listStaff: () => request('/staff').then((data) => ensureArray(data, ['staff'])),
  addStaff: (staff) => request('/staff', { method: 'POST', body: staff }),
  updateStaff: (id, patch) => request(`/staff/${id}`, { method: 'PATCH', body: patch }),

  listSlots: (params = {}) => request(`/slots${query(params)}`),
  slotsSummary: () => request('/slots/summary'),
  addSlot: (slot) => request('/slots', { method: 'POST', body: slot }),
  generateSlots: (payload) => request('/slots/bulk', { method: 'POST', body: payload }),
  clearWeekdays: (staffId, weekdays) =>
    request(`/slots/bulk-clear${query({ staff_id: staffId, weekdays: weekdays.join(',') })}`, { method: 'DELETE' }),
  deleteSlot: (id) => request(`/slots/${id}`, { method: 'DELETE' }),
  blockSlot: (id) => request(`/slots/${id}/block`, { method: 'POST' }),
  unblockSlot: (id) => request(`/slots/${id}/unblock`, { method: 'POST' }),
  blockRange: (payload) => request('/slots/block-range', { method: 'POST', body: payload }),

  // Appointments
  listAppointments: () => request('/appointments').then((data) => ensureArray(data, ['appointments'])),
  markServiceTaken: (id) => request(`/appointments/${id}/service-taken`, { method: 'POST' }),
  markNoShow: (id) => request(`/appointments/${id}/no-show`, { method: 'POST' }),
  setAppointmentStatus: (id, status) => request(`/appointments/${id}/status`, { method: 'POST', body: { status } }),
  createAppointment: (payload) => request('/appointments', { method: 'POST', body: payload }),
  assignOptions: (id) => request(`/appointments/${id}/assign-options`),
  assignStylist: (id, slotId, notify = true) => request(`/appointments/${id}/assign`, { method: 'POST', body: { slot_id: slotId, notify } }),

  // Schedule (Staff Availability → Day schedule, walk-ins)
  scheduleDay: (day) => request(`/schedule/day${query({ day })}`),
  scheduleNow: () => request('/schedule/now'),
  lookupCustomer: (phone) => request(`/customers/lookup${query({ phone })}`),
  knownServices: () => request('/services/known'),

  // Conversations and human intervention
  listConversations: (channel = 'whatsapp') =>
    request(`/conversations${query({ channel })}`).then((data) => ensureArray(data, ['conversations', 'threads'])),
  listMessages: (customerId, channel = 'whatsapp') => request(`/conversations/${customerId}/messages${query({ channel })}`),
  sendReply: (customerId, body, channel = 'whatsapp') =>
    request(`/conversations/${customerId}/reply`, { method: 'POST', body: { body, channel } }),
  pauseAgent: (customerId) => request(`/conversations/${customerId}/pause-agent`, { method: 'POST' }),
  resumeAgent: (customerId) => request(`/conversations/${customerId}/resume-agent`, { method: 'POST' }),
  resetMemory: (customerId) => request(`/conversations/${customerId}/reset-memory`, { method: 'POST' }),

  listHandoffs: (status = 'open') => request(`/handoffs${query({ status })}`),
  resolveHandoff: (id) => request(`/handoffs/${id}/resolve`, { method: 'POST' }),
  getAlertNumbers: () => request('/handoffs/alert-numbers'),
  saveAlertNumbers: (numbers) => request('/handoffs/alert-numbers', { method: 'PUT', body: { numbers } }),
  testAlert: () => request('/handoffs/alert-numbers/test', { method: 'POST' }),

  // Leads and calls
  listLeads: () => request('/leads').then((data) => ensureArray(data, ['leads'])),
  updateLead: (id, patch) => request(`/leads/${id}`, { method: 'PATCH', body: patch }),
  listCalls: () => request('/calls').then((data) => ensureArray(data, ['calls'])),

  // AI Voice Call screen: the Sarvam agent rings a number
  outboundCallConfig: () => request('/outbound-calls/config'),
  startOutboundCall: (phone) => request('/outbound-calls', { method: 'POST', body: { phone } }),
  outboundCallStatus: (attemptId) => request(`/outbound-calls/status${query({ attempt_id: attemptId })}`),

  // Automation (WhatsApp jobs)
  listJobs: (params = {}) => request(`/jobs${query(params)}`).then((data) => ensureArray(data, ['jobs'])),
  cancelJob: (id) => request(`/jobs/${id}/cancel`, { method: 'POST' }),
  cancelJobs: (ids) => request('/jobs/cancel-bulk', { method: 'POST', body: { ids } }),
  listTemplates: (refresh = false) => request(`/templates${query({ refresh: refresh ? 'true' : '' })}`),
  getAutomationSettings: () => request('/automation/settings'),
  saveAutomationSettings: (changes) => request('/automation/settings', { method: 'PUT', body: changes }),
  resetAutomationSettings: () => request('/automation/settings/reset', { method: 'POST' }),

  // Service files for the WhatsApp agent, kept in the OpenAI vector store
  listRag: () => request('/rag'),
  uploadRag: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/rag', { method: 'POST', form });
  },
  deleteRag: (fileId) => request(`/rag/${encodeURIComponent(fileId)}`, { method: 'DELETE' }),
  createVectorStore: () => request('/rag/vector-store', { method: 'POST' }),
};

export default api;

// ── formatting ───────────────────────────────────────────────────────────────

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return value ? String(value) : '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(value) {
  const d = toDate(value);
  if (!d) return value ? String(value) : '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatTime(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export function formatPhone(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  if (raw.startsWith('+')) return raw;
  if (/^\d+$/.test(raw)) return raw.length === 10 ? `+91${raw}` : `+${raw}`;
  return raw;
}

/** Local calendar date as YYYY-MM-DD (not UTC). */
export function isoDate(d = new Date()) {
  const local = new Date(d);
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toISOString().slice(0, 10);
}

export function addDays(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}
